import { getStoredRole } from "auth/session";
import { useCallback } from "react";
import { Departments, expenseTypes, UserRoles } from "../lib/constants";
import { formatAmount } from "../lib/utils";
import { staticSalaryRecords } from "./staticData";
import useApiService from "./useApiService";

const staticAllVouchers = Array.from({ length: 20 }).map((_, idx) => {
  const id = idx + 1;
  const employee_id = idx + 1;
  const employee_name = `Employee ${idx + 1}`;
  const amount_requested = formatAmount(Math.floor(Math.random() * 10000) + 1);
  const applied_on = new Date(Date.now() + Math.floor(Math.random() * 1000 * 60 * 60 * 24)).toISOString().split("T")[0];
  const request_type = expenseTypes.sort(() => Math.random() - 0.5)[0];
  const statusValues = ["active", "approved", "rejected"];
  const status = statusValues[Math.floor(Math.random() * statusValues.length)];
  const department = Object.values(Departments)[Math.floor(Math.random() * Object.values(Departments).length)];
  const date =
    status === "active"
      ? applied_on
      : new Date(Date.now() + Math.floor(Math.random() * 1000 * 60 * 60 * 24)).toISOString().split("T")[0];
  const amount_reimbursed = status === "approved" ? formatAmount(Math.floor(Math.random() * 10000) + 1) : null;
  const reason_for_rejection = status === "rejected" ? `Reason for rejection of voucher ${idx + 1}` : null;
  const note = "Lorem ipsum dolor sit amet consectetur adipisicing elit. Suscipit, est!";
  const isReimbursed = status === "approved" ? [true, false][Math.floor(Math.random() * 2)] : false;

  return {
    id,
    employee_id,
    employee_name,
    department,
    amount_requested,
    applied_on,
    status,
    date,
    amount_reimbursed,
    reason_for_rejection,
    request_type,
    note,
    isReimbursed,
  };
});

const staticBillRecords = Array.from({ length: 20 }).map((_, idx) => {
  const id = idx + 1;
  const employee_id = 1;
  const employee_name = "Employee 1";
  const amount_requested = formatAmount(Math.floor(Math.random() * 10000) + 1);
  const applied_on = new Date(Date.now() + Math.floor(Math.random() * 1000 * 60 * 60 * 24)).toISOString().split("T")[0];
  const request_type = ["Telephone Bill", "Taxes & Insurance", "Monthly Rent", "EMI"].sort(
    () => Math.random() - 0.5
  )[0];
  const statusValues = ["active", "approved", "rejected"];
  const status = statusValues[Math.floor(Math.random() * statusValues.length)];
  const department = "HR";
  const date =
    status === "active"
      ? applied_on
      : new Date(Date.now() + Math.floor(Math.random() * 1000 * 60 * 60 * 24)).toISOString().split("T")[0];
  const amount_paid = status === "approved" ? formatAmount(Math.floor(Math.random() * 10000) + 1) : null;
  const reason_for_rejection = status === "rejected" ? `Reason for rejection of voucher ${idx + 1}` : null;
  const isPaid = status === "approved" ? [true, false][Math.floor(Math.random() * 2)] : false;
  const note = "Lorem ipsum dolor sit amet consectetur adipisicing elit. Suscipit, est!";

  return {
    id,
    employee_id,
    employee_name,
    department,
    amount_requested,
    applied_on,
    status,
    date,
    amount_paid,
    reason_for_rejection,
    request_type,
    isPaid,
    note,
    payment_method: ["Cash", "Cheque", "Online Transfer", "Credit Card", "Debit Card"][Math.floor(Math.random() * 5)],
    bill_proof: "https://example.com/bill_proof.pdf",
    receipt_proof: "https://example.com/receipt_proof.pdf",
  };
});

const useAccounts = () => {
  const { get } = useApiService();
  const checkRole = useCallback(() => {
    return getStoredRole();
  }, []);

  const fetchData = useCallback(
    async (url) => {
      try {
        const response = await get(url);
        if (response.status) return { success: response.status, data: response.data };
        else throw new Error(response.message);
      } catch (error) {
        return { error };
      }
    },
    [get]
  );

  const getAllSalaryRecordsByMonth = useCallback(
    async (month, year) => {
      // if (!month || !year) return { error: "Month and year are required." };

      const userRole = checkRole();
      const allowedRoles = [UserRoles.ADMIN, UserRoles.ACCOUNTS];
      if (!allowedRoles.includes(userRole)) {
        return { error: "You are not authorized to access this page." };
      }
      const apiUrl = {
        [UserRoles.ADMIN]: `/api/hr/release-salaries/${year}/${month}`,
        [UserRoles.ACCOUNTS]: `/api/accounts/debits/salary`,
      };

      return { salaryRecords: staticSalaryRecords };

      return fetchData(apiUrl[userRole]);
    },
    [checkRole, fetchData]
  );

  const getAllVouchersData = useCallback(async () => {
    const userRole = checkRole();
    const allowedRoles = [UserRoles.ADMIN, UserRoles.ACCOUNTS];
    if (!allowedRoles.includes(userRole)) {
      return { error: "You are not authorized to access this page." };
    }
    const apiUrl = {
      [UserRoles.ADMIN]: "/api/hr/reimbursement-requests",
      [UserRoles.ACCOUNTS]: "/api/accounts/debits/reimbursements",
    };

    return userRole === UserRoles.ACCOUNTS
      ? { vouchers: staticAllVouchers.filter((voucher) => voucher.status === "approved") }
      : { vouchers: staticAllVouchers };

    return fetchData(apiUrl[userRole]);
  }, [checkRole, fetchData]);

  const getAllBillRecords = useCallback(async () => {
    const userRole = checkRole();
    const allowedRoles = [UserRoles.ADMIN, UserRoles.ACCOUNTS];
    if (!allowedRoles.includes(userRole)) {
      return { error: "You are not authorized to access this page." };
    }
    const apiUrl = {
      [UserRoles.ADMIN]: "/api/accounts/debits/billings",
      [UserRoles.ACCOUNTS]: "/api/accounts/debits/billings",
    };

    return userRole === UserRoles.ACCOUNTS
      ? { billRecords: staticBillRecords.filter((bill) => bill.status === "approved") }
      : { billRecords: staticBillRecords };

    return fetchData(apiUrl[userRole]);
  }, [checkRole, fetchData]);

  const getVouchersDataByEmployeeId = useCallback(async (employeeId) => {}, []);

  return {
    getAllSalaryRecordsByMonth,
    getAllVouchersData,
    getVouchersDataByEmployeeId,
    getAllBillRecords,
  };
};

export default useAccounts;
